// AdditionalDetails.tsx
import React, { useEffect, useState } from 'react';
import axios from "../lib/axios";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from 'react-router-dom';

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { API_BASE_URL } from '../config';
import CallbackHelp from "@/components/CallbackHelp";

import {
  stringOrEmptyOptional,
  boundedOptionalNumberLoad,
  boundedNumberSubmit,
  reasonForLoanSchema,
  otherBusinessesSchema,
  pincodeNumberStringOptional,
  optionalReasonForLoanSchema,
  optionalOtherBusinessesSchema,
  addressStringOptional,
} from "@/helpers/validations";
import { useLoader } from "../contexts/LoaderContext";
import { useJourney } from "@/contexts/JourneyContext";
import JourneyHeader from "@/components/JourneyHeader";


// For loading data from backend (can accept nulls/empties, transform as needed)
export const businessFormLoadSchema = z.object({
  years_in_business: boundedOptionalNumberLoad(0, 9999),
  months_in_business: boundedOptionalNumberLoad(0, 11),

  reason_for_loan: optionalReasonForLoanSchema,

  other_businesses: optionalOtherBusinessesSchema,

});

// For submitting data (strict validation, required fields, proper ranges)
export const businessFormSubmitSchema = z.object({
  years_in_business: boundedNumberSubmit(0, 9999),

  months_in_business: boundedNumberSubmit(0, 11),

  reason_for_loan: reasonForLoanSchema,

  other_businesses: otherBusinessesSchema,

});
type FormValues = z.infer<typeof businessFormSubmitSchema>;

const AdditionalDetails = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [fetching, setFetching] = useState(true);
  const { loading, setLoading, setLoaderMessage } = useLoader();
  const { refresh: refreshJourney } = useJourney();

  const form = useForm<FormValues>({
    resolver: zodResolver(businessFormSubmitSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    shouldUnregister: true,
    defaultValues: {
      reason_for_loan: "Working Capital",
    },
  });

  const handleSubmit = (data: FormValues) => {
    setLoading(true);
    setLoaderMessage("additional");
    console.log("Form submitted:", data);
    // In a real app, you would submit this data to an API
    // toast({
    //   title: "Information saved",
    //   description: "Your additional details have been saved successfully.",
    // });
    // Navigate to the loan offer page

    axios
      .post(`${API_BASE_URL}/api/set_additional_details/`, data, { maxRedirects: 0 })
      .then((res) => {
        if (res.request.responseURL.includes("https://apply.intellendtech.com/accounts/login/")) {
          navigate("/session-timeout");
        }
        console.log("Backend response:", res.data);
        console.log("Status:", res.data.status);
        console.log("Message:", res.data.message);
        console.log("Message:", res.data.message);

        if (res.data.next_page_url) {
          navigate(res.data.next_page_url);
        } else {
          // Fallback
          navigate("/ruleset-a");
        }

        // if (res.status === 200 && res.data.enhanced_offer_available === true) {
        //   // alert(res.status + "\n" + res.data.enhanced_offer_available)
        //   navigate("/loan-offer-with-button");
        // } else if(res.status === 200 && res.data.enhanced_offer_available !== true) {  
        //   // alert(res.status + "\n" + res.data.enhanced_offer_available)   
        //   navigate("/loan-offer");
        // }
      })
      .catch((err) => {
        if (confirm("Error saving data: " + err.message + ".\n Press OK to reload the page.")) {
          window.location.reload();
        }
      })
      .finally(() => {
        setLoaderMessage(null);
        setLoading(false);
      });
  };

  const {
    reset,
    setError,
    trigger,
    clearErrors,
    formState: { errors },
  } = form;

  // Fetching data from backend
  // and setting it to the form
  useEffect(() => {
    setFetching(true);
    axios
      .post(`${API_BASE_URL}/api/get_additional_details/`, {}, { maxRedirects: 0 })
      .then(async (res) => {
        if (res.request.responseURL.includes("https://apply.intellendtech.com/accounts/login/")) {
          navigate("/session-timeout");
        }
        console.log("Backend response:", res.data);

        type LoadValues = z.infer<typeof businessFormLoadSchema>;
        type SubmitValues = z.infer<typeof businessFormSubmitSchema>;

        // 1. Validate response against Load Schema
        const loadResult = businessFormLoadSchema.safeParse(res.data);

        if (!loadResult.success) {
          console.error("Load schema validation failed:", loadResult.error);

          // Flatten errors
          const flatErrors = loadResult.error.flatten();
          Object.entries(flatErrors.fieldErrors).forEach(([field, messages]) => {
            if (messages?.length) {
              setError(field as keyof LoadValues, {
                type: "manual",
                message: messages[0],
              });
            }
          });

          return; // stop if data unusable
        }

        // 2. Reset form with clean data
        // clear old errors before reset
        clearErrors();

        const parsedLoadData = loadResult.data;
        // Auto-select "Working Capital" if not already set (only option available)
        if (!parsedLoadData.reason_for_loan) {
          parsedLoadData.reason_for_loan = "Working Capital";
        }
        reset(parsedLoadData, { keepErrors: false, keepDirty: false });

        // ✅ Wait for reset to finish, then trigger validation
        await new Promise((resolve) => setTimeout(resolve, 0));
        // await trigger();
        // 3. Now safely check Submit Schema
        // const currentValues = form.getValues();
        // const submitResult = businessFormSubmitSchema.safeParse(currentValues);

        // if (!submitResult.success) {
        //   console.warn("Submit schema validation failed:", submitResult.error);

        //   const flatErrors = submitResult.error.flatten();
        //   Object.entries(flatErrors.fieldErrors).forEach(([field, messages]) => {
        //     if (messages?.length) {
        //       setError(field as keyof SubmitValues, {
        //         type: "manual",
        //         message: messages[0],
        //       });
        //     }
        //   });
        // }

        // // Debug after RHF finishes updating formState
        // // await new Promise((resolve) => setTimeout(resolve, 0));
        console.log("Form errors after submit schema check:", form.formState.errors);
      })
      .catch((err) => console.error("Fetch error:", err))
      .finally(() => setFetching(false));
  }, []);

  if (fetching) return null;

  return (
      <div className="min-h-screen bg-intellend-white">
        {/* Header with logo */}
        <JourneyHeader />

        {/* Main content */}
        <main className="container mx-auto px-4 py-8 max-w-3xl">
          <div className="bg-white shadow-md rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-intellend-green mb-2">Additional Information</h2>
            <p className="text-gray-600 mb-6">We just need a few more details to proceed</p>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                {/* Time in Business */}
                <div>
                  <FormLabel className="block mb-2">Time in Business</FormLabel>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="years_in_business"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Years <span className="text-red-500">*</span></FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              inputMode="numeric"
                              min="0"
                              placeholder="Years"
                              {...field}
                              value={field.value ?? ""}
                              onChange={(e) => field.onChange(e.target.value)}
                              showSuccess={field.value !== undefined && field.value !== null && field.value !== "" && !form.formState.errors.years_in_business}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="months_in_business"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Months <span className="text-red-500">*</span></FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              inputMode="numeric"
                              min="0"
                              max="11"
                              placeholder="Months"
                              {...field}
                              value={field.value ?? ""}
                              onChange={(e) => field.onChange(e.target.value)}
                              showSuccess={field.value !== undefined && field.value !== null && field.value !== "" && !form.formState.errors.months_in_business}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Reason for Loan */}
                <FormField
                  control={form.control}
                  name="reason_for_loan"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Reason for Applying for Loan <span className="text-red-500">*</span></FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger showSuccess={!!field.value && !form.formState.errors.reason_for_loan}>
                            <SelectValue placeholder="Select reason" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Working Capital">Working Capital</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Other Businesses Owned */}
                <FormField
                  control={form.control}
                  name="other_businesses"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Any Other Businesses Owned by You <span className="text-red-500">*</span></FormLabel>
                      <div className="text-sm text-gray-500">If not associated with any other businesses, select zero. </div>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger showSuccess={!!field.value && !form.formState.errors.other_businesses}>
                            <SelectValue placeholder="Select number of OTHER businesses" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="zero">0 [No Other Business]</SelectItem>
                          <SelectItem value="one">1</SelectItem>
                          <SelectItem value="twoToThree">2-3</SelectItem>
                          <SelectItem value="threeOrMore">3+</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* Alternate Address */}

                <div className="flex flex-col md:flex-row gap-4 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsCallbackOpen(true)}
                    className="w-full md:w-auto"
                  >
                    Request Callback
                  </Button>
                  <Button
                    type="submit"
                    className="w-full md:w-auto md:ml-auto bg-intellend-green hover:bg-intellend-green/90"
                    disabled={!form.formState.isValid}
                  >
                    Next
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </main>

        {/* Callback Dialog */}
        <CallbackHelp
          isOpen={isCallbackOpen}
          setIsOpen={setIsCallbackOpen}
          showOk={false}
          showCallUs={true}
          showWhatsappUs={true}
          silentlyTrigger={true}
          title="Intellend Support"
        />
      </div>
    );
};

export default AdditionalDetails;
